<template>
  <SkeleLoader
      v-if="isNftLoading || !isRouteReady"
      :lines="10"
      :hasContentImg="true"
      contentIsFullWidth
    />
  <div class="nft-preview" v-else>
    <div class="nft-preview__image">
      <img v-if="nftDataStore.getNftData.image && !nftDataStore.getNftData.video" ref="target" :src="nftDataStore.getNftData.image" />
      <video v-if="nftDataStore.getNftData.video" :src="nftDataStore.getNftData.video" autoplay muted />
    </div>
    <div class="nft-preview__content">
      <div class="nft-preview__titles">
        <h3 class="small-headline">{{ $t('NFTPreview.label.aboutToBuy') }}</h3>
        <h1 class="large-headline">{{nftDataStore.getNftData.name}}</h1>
        <p class="bodycopy">{{nftDataStore.getNftData.collection}}</p>
      </div>
      <div class="nft-preview__cost" v-if="isOrderReady">
        <div class="nft-preview__amount">
          <div class="nft-preview__currency">
            <div class="nft-preview__currency__icon">
              <span
              :class="`currency-flag currency-flag-${
                fiatType && fiatType?.toLowerCase()
              }`"
              ></span>
            </div>
            <h1 class="small-headline">{{fiatType}}</h1>
          </div>
          <h1 class="large-headline">{{ toCurrencyFormat(convertFiatAmountToDecimals, fiatType) }}</h1>
        </div>
        <p class="bodycopy">+ ${{feesForOrder}} {{ $t('NFTPreview.label.fees') }}</p>
      </div>
      <SkeleLoader
        v-else
        :lines="2"
        :hasContentImg="false"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { whenever } from "@vueuse/core";
import { useI18n } from 'vue-i18n';

import useApiHelpers from "@/composables/api/helpers";
import { useNFTDataStore } from "@/stores/forms/nftDataStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import { toCurrencyFormat } from "@/composables/helpers/useStringHelpers";
import useErrorHandling from "@/composables/api/useErrorHandling";

import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";

const route = useRoute();
const nftDataStore = useNFTDataStore();
const orderStore = useOrderFormStore();
const { getRequest } = useApiHelpers();
const { handleErrorInSpaFlow } = useErrorHandling();
const { t } = useI18n();

const loadOrderDetailsFromOrderStoreRoutes = [
  "verification",
  "verificationCode",
  "nft.order",
  "nft.order.transit",
]

const loadNftFromApiRoutes = [
  "nft.order",
  "nft.order.transit"
]

const isNftLoading = ref(true);
const isOrderReady = ref(false);

const isRouteReady = computed(() => route.name ? true : false)
const shouldLoadOrderFromAccordion = computed(() => route.name && !loadOrderDetailsFromOrderStoreRoutes.includes(route.name));
const opacity = computed(() => {
  return route.name?.includes("summary") ? 0.12 : 1;
});
const display = computed(() => {
  return route.name?.includes("summary") ? "none" : "flex";
});
const fiatType = computed(() => 
  route.name && loadOrderDetailsFromOrderStoreRoutes.includes(route.name)
    ? orderStore.getActiveStore.form.fiatType.value
    : orderStore.orderAccordionData.fiatType);
const convertFiatAmountToDecimals = computed(() => {
  const fiatAmount = route.name && loadOrderDetailsFromOrderStoreRoutes.includes(route.name)
    ? orderStore.getActiveStore.form.fiatAmount.value
    : orderStore.orderAccordionData.fiatAmount;
  return parseFloat(fiatAmount).toFixed(2)
});
const feesForOrder = computed(() => {
  const fees = route.name && loadOrderDetailsFromOrderStoreRoutes.includes(route.name)
    ? (parseFloat(orderStore.getActiveStore.feeData.networkFee) + parseFloat(orderStore.getActiveStore.feeData.gatewayFee))
    : orderStore.orderAccordionData.gatewayFee + orderStore.orderAccordionData.networkFee;
  return parseFloat(fees).toFixed(2)
})

// on mount cannot access route.name, have to use a compute object to detect when the route is ready
whenever(isRouteReady, () => {
  // for order page, the NFT is loaded from API or URL, otherwise, load it from local storage
  if (route.name && !loadNftFromApiRoutes.includes(route.name)){
    nftDataStore.setNFTDataFromLocalStorage();
  }
}, {immediate: true})

// watch when nft is set, disable the SkeleLoader
watch(() => nftDataStore.getNftData, (nftData) => {
  if (nftData.name){
    isNftLoading.value = false
  }
}, {deep: true, immediate: true})

watch(() => orderStore.getActiveStore.form.fiatAmount.value, (fiatAmount) => {
  if (route.name && loadOrderDetailsFromOrderStoreRoutes.includes(route.name) && fiatAmount){
    isOrderReady.value = true;
  }
}, {deep: true, immediate: true})

whenever(shouldLoadOrderFromAccordion, async () => {
  if (route.name && loadOrderDetailsFromOrderStoreRoutes.includes(route.name)){
    return;
  }
  const data = await getRequest("/summary");
  if (data){
    const orderSummary = data.data.data;
    if (!orderSummary.orderType) {
      handleErrorInSpaFlow(40441, t('NFTPreview.error.40441'));
      return;
    }
    // if order summary data is not NFT mode, set it to another mode and quit
    if (["CRYPTO-BUY", "CRYPTO-SELL"].includes(orderSummary.orderType.toUpperCase())){
      const orderMode = orderSummary.orderType.toUpperCase() === "CRYPTO-SELL" ? "SELL" : "BUY";
      orderStore.setActiveMode(orderMode);
      return;
    }
    Object.entries(orderSummary).forEach(([key, value]) => {
      if (value && key !== "nft") orderStore.updateOrderAccordion(key, value);
    });
    // refresh NFT data with summary data if it exist
    if (orderSummary.nft){
      nftDataStore.setNftData("name", orderSummary.nft?.name);
      nftDataStore.setNftData("collection", orderSummary.nft?.collectionName);
      nftDataStore.setNftData("image", orderSummary.nft?.image);
      nftDataStore.setNftData("video", orderSummary.nft?.video);
    }
    isOrderReady.value = true;
  }
})

</script>

<style lang="scss">
.nft-preview {
  display: v-bind(display);
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  margin: 0 auto 2.4rem;
  padding: 0 var(--padding-1);
  max-width: 54rem;
  opacity: v-bind(opacity);
  transition: opacity 250ms;

  @include breakpoint($min-m) {
    margin-bottom: 0;
  }

  & > div {
    width: calc(50% - 0.8rem);
  }

  &__image {
    display: flex;
    justify-content: center;

    img {
      width: 100%;
      align-self: center;
      border-radius: 1rem;

      @include breakpoint($min-m) {
        max-width: 25rem;
        margin-bottom: 2.4rem;
      }
    }

    video {
      width: 100%;
      align-self: center;
      border-radius: 1rem;

      @include breakpoint($min-m) {
        max-width: 25rem;
        margin-bottom: 2.4rem;
      }
    }
  }

  &__content {
    text-align: right;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @include breakpoint($min-m) {
      text-align: center;
      justify-content: center;
    }
  }

  &__titles {
    .small-headline {
      font-size: 1.2rem;
      color: var(--secondary);
      margin-bottom: 0.6rem;
    }

    @include breakpoint($min-m) {
      margin-bottom: 3rem;

      .large-headline {
        font-size: 3.2rem;
        margin-bottom: 0.8rem;
      }
    }
  }

  &__amount {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;

    @include breakpoint($min-m) {
      justify-content: center;
    }

    .large-headline {
      padding-left: 1rem;
    }
  }

  &__currency {
    display: flex;
    align-items: center;
    padding: 0.4rem 0.8rem;
    border: 2px solid rgba(255, 255, 255, 0.16);
    border-radius: 0.4rem;

    &__icon {
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      overflow: hidden;
      position: relative;

      span {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.5);
        transform-origin: 50% 50%;
      }
    }
  }
}
</style>
